// src/components/templates/organisation/OrganisationPortal.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom';
import { 
  Users, Building2, ClipboardList, MapPin, FileText, Package, MessageCircle,
  Database, Settings, Menu, X, Home, BarChart3, Bell, Search, Calendar,
  ChevronDown, User, LogOut, AlertTriangle
} from 'lucide-react';
import { organisationService } from '../../../services/organisationService';

import { TeamFormFields } from '../../manager/shared/TeamFormFields';
import { AssetFormFields } from '../../manager/shared/AssetFormFields';
import { TaskFormFields} from '../../manager/shared/TaskFormFields';
import { SiteFormFields } from '../../manager/shared/SiteFormFields';
import { ClientFormFields } from '../../manager/shared/ClientFormFields';
import { DocumentFormFields } from '../../manager/shared/DocumentFormFields';
import { ResourcesFormFields } from '../../manager/shared/ResourcesFormFields';
import { RecordsFormFields } from '../../manager/shared/RecordsFormFields';
import { PostsSectionForm } from '../../manager/shared/PostsSectionForm';
import { SharedHeader } from '../../shared/SharedHeader';

// Import shared components
import { OrganisationOverview } from './OrganisationOverview';
import { OrganisationDashboard } from './OrganisationDashboard';
import { BookingSystemForm } from '../../manager/booking/BookingSystemForm';

const MANAGEMENT_COMPONENTS = {
  team: {
    component: TeamFormFields,
    title: 'Team Management',
    icon: Users,
    color: 'blue'
  },
  assets: {
    component: AssetFormFields,
    title: 'Asset Management',
    icon: Building2,
    color: 'green'
  },
  tasks: {
    component: TaskFormFields,
    title: 'Task Management',
    icon: ClipboardList,
    color: 'orange'
  },
  sites: {
    component: SiteFormFields,
    title: 'Site Management',
    icon: MapPin,
    color: 'pink'
  },
  clients: {
    component: ClientFormFields,
    title: 'Client Management',
    icon: Users,
    color: 'indigo'
  },
  documents: {
    component: DocumentFormFields,
    title: 'Document Management',
    icon: FileText,
    color: 'yellow'
  },
  resources: {
    component: ResourcesFormFields,
    title: 'Resources Management',
    icon: Package,
    color: 'purple'
  },
  records: {
    component: RecordsFormFields,
    title: 'Records Management',
    icon: Database,
    color: 'violet'
  },

  posts: {
    component: PostsSectionForm,
    title: 'Internal Posts',
    icon: MessageCircle,
    color: 'gray'
  },

  booking: {
    component: BookingSystemForm,
    title: 'Booking Management',
    icon: Calendar,
    color: 'purple'
  }
};

export const OrganisationPortal = ({ bookData, user, bookId, formData, onUpdate }) => {
  const navigate = useNavigate();
  const userProfile = user?.profile || null;
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [managementConfig, setManagementConfig] = useState({});
  const [managementFeatures, setManagementFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    loadPortalData();
  }, [bookId]);

  const loadPortalData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load management configuration from pb-management subcollection
      const configData = await organisationService.getManagementConfig(bookId);
      

      // Load all management feature data
      const featureData = {};
      const enabledFeatures = getEnabledFeaturesFromBook();

      setIsOwner(bookData.ownerId === user?.uid);
      
      for (const feature of enabledFeatures) {
        try {
          const data = await organisationService.getManagementFeatureData(bookId, feature);
          if (data) {
            featureData[feature] = data;
          }
        } catch (error) {
          console.warn(`Could not load ${feature} data:`, error);
        }
      }

      setManagementConfig(configData || {});
      setManagementFeatures(featureData);
      
      // Load notifications if enabled
      await loadNotifications();
      
    } catch (error) {
      console.error('Error loading portal data:', error);
      setError('Failed to load management portal. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      if (!formData?.enableManagementNotifications) return;
      
      // Get recent activities from records management
      const recordsData = await organisationService.getManagementFeatureData(bookId, 'records');
      const recentActivities = recordsData?.activities?.slice(0, 5) || [];
      setNotifications(recentActivities);
    } catch (error) {
      console.warn('Could not load notifications:', error);
    }
  };

  // Get enabled features from book configuration (from OrganisationManager settings)
  const getEnabledFeaturesFromBook = () => {
    const features = [];
    
    // Add existing management features
    if (bookData?.managementFeatures) {
      Object.keys(bookData.managementFeatures).forEach(feature => {
        if (bookData.managementFeatures[feature]?.enabled) {
          features.push(feature);
        }
      });
    }
    
    // Add booking if enabled
    if (bookData?.enableBooking && bookData?.enableBookingPortal) {
      features.push('booking');
    }
    
    return features;
  };

  const getEnabledFeatures = () => {
    return getEnabledFeaturesFromBook();
  };

  const getFeatureConfig = (featureKey) => {
    return bookData?.managementFeatures?.[featureKey] || {};
  };

  const getFeatureData = (featureKey) => {
    return managementFeatures[featureKey] || null;
  };

  const getFeatureStats = (featureKey) => {
    const data = getFeatureData(featureKey);
    if (!data) return null;

    switch (featureKey) {
      case 'team':
        return {
          total: data.totalMembers || data.members?.length || 0,
          active: data.activeMembers || data.members?.filter(m => m.isActive)?.length || 0
        };
      case 'sites':
        return {
          total: data.totalSites || data.sites?.length || 0,
          active: data.activeSites || data.sites?.filter(s => s.status === 'active')?.length || 0
        };
      case 'assets':
        return {
          total: data.totalAssets || data.assets?.length || 0,
          active: data.activeAssets || data.assets?.filter(a => a.status === 'active')?.length || 0
        };
      case 'tasks':
        return {
          total: data.totalTasks || data.tasks?.length || 0,
          active: data.activeTasks || data.tasks?.filter(t => t.status !== 'completed')?.length || 0
        };
      default:
        return {
          total: Object.keys(data).length,
          active: 0
        };
    }
  };

  const renderSidebar = () => {
    const enabledFeatures = getEnabledFeatures();

    return (
      <div className={`fixed inset-y-0 left-0 z-50 w-80 bg-white shadow-lg transform ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {bookData?.logoUrl ? (
              <img 
                src={bookData.logoUrl} 
                alt="Logo"
                className="h-8 w-auto object-cover rounded-lg"
              />
            ) : (
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-bold text-gray-900 truncate">
                {bookData?.botName || 'Organization'}
              </h1>
              {bookData?.tagline && (
                <p className="text-xs text-gray-600 truncate">{bookData.tagline}</p>
              )}
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <div className="space-y-1">
            {/* Core Navigation */}
            <SidebarItem
              icon={Home}
              label="Overview"
              isActive={activeSection === 'overview'}
              onClick={() => setActiveSection('overview')}
            />
            
            <SidebarItem
              icon={BarChart3}
              label="Dashboard"
              isActive={activeSection === 'dashboard'}
              onClick={() => setActiveSection('dashboard')}
            />

            {/* Management Features */}
            {enabledFeatures.length > 0 && (
              <>
                <div className="px-3 py-2 mt-6">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Management
                  </h3>
                </div>
                
                {enabledFeatures.map(featureKey => {
                  const component = MANAGEMENT_COMPONENTS[featureKey];
                  const config = getFeatureConfig(featureKey);
                  const stats = getFeatureStats(featureKey);
                  
                  if (!component) return null;
                  
                  return (
                    <SidebarItem
                      key={featureKey}
                      icon={component.icon}
                      label={config.displayName || component.title}
                      isActive={activeSection === featureKey}
                      onClick={() => setActiveSection(featureKey)}
                      color={component.color}
                      accessLevel={config.accessLevel}
                      stats={stats}
                    />
                  );
                })}
              </>
            )}

            {/* Records Management - Always show if enabled for audit visibility */}
            {enabledFeatures.includes('records') && (
              <>
                <div className="px-3 py-2 mt-6">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Audit & Compliance
                  </h3>
                </div>
                <SidebarItem
                  icon={Database}
                  label="Activity Records"
                  isActive={activeSection === 'records'}
                  onClick={() => setActiveSection('records')}
                  color="gray"
                />
              </>
            )}

            {bookData?.enableBooking && bookData?.enableBookingPortal && (
              <>
                <div className="px-3 py-2 mt-6">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Booking System
                  </h3>
                </div>
                <SidebarItem
                  icon={Calendar}
                  label="Booking Management"
                  isActive={activeSection === 'booking'}
                  onClick={() => setActiveSection('booking')}
                  color="purple"
                  stats={{ count: '12 today' }} // Could be dynamic
                />
              </>
            )}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="border-t border-gray-200 p-4">
          {user.uid && userProfile ? (
            <div className="flex items-center gap-3">
              {userProfile.photoURL ? (
                <img
                  src={userProfile.photoURL}
                  alt={userProfile.displayName || 'User'}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {(userProfile.displayName || userProfile.email || 'U')[0].toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {userProfile.displayName || 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {userProfile.email}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-gray-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  Guest User
                </p>
                <p className="text-xs text-gray-500">
                  Not signed in
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderHeader = () => {
    // Header configuration for organization portal
    const headerConfig = {
      showLogo: true,
      showTitle: true,
      showDescription: false,
      showSearch: true,
      showNotifications: true,
      showOwnerControls: isOwner,
      userProfileVisibility: 'authenticated',
      searchPosition: 'right',
      searchPlaceholder: 'Search organization...'
    };

    // Styling from book data
    const styling = {
      primaryColor: bookData?.primaryColour || '#2563eb',
      headerTextColor: bookData?.textColour || '#111827',
      headerBackground: '#ffffff'
    };

    // Header actions
    const actions = {
      onSearchChange: (query) => setSearchQuery(query),
      onNotificationClick: () => {
        console.log('Notifications clicked');
      },
      onSettingsClick: () => {
        setActiveSection('settings');
      }
    };

    // Header state
    const state = {
      searchQuery: searchQuery || '',
      notificationCount: 0
    };

    return (
      <div className="sticky top-0 z-30 bg-white shadow-sm border-b border-gray-200">
        <SharedHeader
          templateType="organisation"
          bookData={bookData}
          styling={styling}
          isOwner={isOwner}
          navigate={navigate}
          headerConfig={headerConfig}
          actions={actions}
          state={state}
        >
          {/* Mobile menu toggle */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
        </SharedHeader>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading management portal...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Portal</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadPortalData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    // Core sections
    if (activeSection === 'overview') {
      return (
        <OrganisationOverview 
          bookData={bookData}
          formData={formData}
          managementConfig={managementConfig}
          managementFeatures={managementFeatures}
          onNavigate={setActiveSection}
        />
      );
    }

    if (activeSection === 'dashboard') {
      return (
        <OrganisationDashboard
          bookData={bookData}
          formData={formData}
          managementConfig={managementConfig}
          managementFeatures={managementFeatures}
          enabledFeatures={getEnabledFeatures()}
          onNavigate={setActiveSection}
        />
      );
    }

    // Management feature sections
    const managementComponent = MANAGEMENT_COMPONENTS[activeSection];
    if (managementComponent) {
      const Component = managementComponent.component;
      const featureConfig = getFeatureConfig(activeSection);
      
      return (
        <div className="p-6">
          <Component
            bookId={bookId}
            templateType="organisation"
            onUpdate={loadPortalData}
            accessLevel={featureConfig.accessLevel || 'internal'}
            featureConfig={featureConfig}
            searchQuery={searchQuery}
          />
        </div>
      );
    }

    // Fallback
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Section Not Found</h3>
          <p className="text-gray-600 mb-4">The requested section is not available or not configured.</p>
          <button
            onClick={() => setActiveSection('overview')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go to Overview
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      {renderSidebar()}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        {renderHeader()}

        {/* Content */}
        <main className="flex-1 overflow-y-auto">
          {renderContent()}
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
        </div>
      )}
    </div>
  );
};

// Enhanced Sidebar Item Component
const SidebarItem = ({ 
  icon: Icon, 
  label, 
  isActive, 
  onClick, 
  color = 'gray', 
  accessLevel, 
  stats 
}) => {
  const getColorClasses = () => {
    if (isActive) {
      return {
        'blue': 'bg-blue-100 text-blue-700 border-r-2 border-blue-600',
        'green': 'bg-green-100 text-green-700 border-r-2 border-green-600',
        'orange': 'bg-orange-100 text-orange-700 border-r-2 border-orange-600',
        'pink': 'bg-pink-100 text-pink-700 border-r-2 border-pink-600',
        'indigo': 'bg-indigo-100 text-indigo-700 border-r-2 border-indigo-600',
        'yellow': 'bg-yellow-100 text-yellow-700 border-r-2 border-yellow-600',
        'purple': 'bg-purple-100 text-purple-700 border-r-2 border-purple-600',
        'gray': 'bg-gray-100 text-gray-700 border-r-2 border-gray-600'
      }[color] || 'bg-gray-100 text-gray-700 border-r-2 border-gray-600';
    }
    return 'text-gray-600 hover:text-gray-900 hover:bg-gray-50';
  };

  const getIconColorClasses = () => {
    if (isActive) {
      return {
        'blue': 'text-blue-600',
        'green': 'text-green-600',
        'orange': 'text-orange-600',
        'pink': 'text-pink-600',
        'indigo': 'text-indigo-600',
        'yellow': 'text-yellow-600',
        'purple': 'text-purple-600',
        'gray': 'text-gray-600'
      }[color] || 'text-gray-600';
    }
    return 'text-gray-400';
  };

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${getColorClasses()}`}
    >
      <Icon className={`h-5 w-5 ${getIconColorClasses()}`} />
      <span className="truncate flex-1 text-left">{label}</span>
      
      {/* Stats Badge */}
      {stats && (
        <div className="flex flex-col items-center">
          <span className="text-xs font-medium">{stats.total}</span>
          {stats.active !== undefined && stats.active !== stats.total && (
            <span className="text-xs text-gray-500">({stats.active})</span>
          )}
        </div>
      )}
      
      {/* Access Level Badge */}
      {accessLevel && accessLevel !== 'internal' && (
        <span className={`px-1 py-0.5 text-xs rounded ${
          accessLevel === 'public' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
        }`}>
          {accessLevel}
        </span>
      )}
    </button>
  );
};